<template>
  <div class="response-list-item__respondent-info">
    <!--------------------------------------------
    |
    | FETCHING SPINNER
    |
    --------------------------------------------->
    <Spinner
      :is-loading="isFetching"
      :componentStyle="true"
      message="fetching row info..."
      v-if="isFetching"
    />

    <!--------------------------------------------
    |
    | MAIN CONTENT
    |
    --------------------------------------------->
    <template v-if="!isFetching && data">
      <MessageBlock
        class="response-list-item__respondent-info-empty"
        message-type="information"
        title="No row info"
        v-if="isDataEmpty"
      >
        <VisibleText
          >Sorry, we could find no information for this row. Are you sure categories are included in
          your data and properly defined in the data uploading process?</VisibleText
        >
      </MessageBlock>

      <div class="response-list-item__responseinfo-wrapper" v-else>
        <div class="respondent-info">
          <!-- SUBMISSION TIME -->
          <div v-if="dataSubmissionTime" class="info-card submission-card">
            <div class="info-card-title">
              <span class="icon">🕒</span>
              <VisibleText>Submitted</VisibleText>
            </div>
            <div class="info-card-value">
              {{ dataSubmissionTime }}
            </div>
          </div>

          <!-- CATEGORIES -->
          <div v-if="dataCategories" class="info-card">
            <div class="info-card-title">
              <span class="icon">🏷️</span>
              <VisibleText>Categories</VisibleText>
            </div>

            <ul class="info-list">
              <li v-for="(cat, index) in dataCategories" :key="index">
                <span class="label">{{ cat.question_title }}:</span>
                <span class="value">{{ cat.value }}</span>
              </li>
            </ul>
          </div>

          <!-- OUTLIERS -->
          <div v-if="dataOutliers" class="info-card">
            <div class="info-card-title">
              <span class="icon">⚠️</span>
              <VisibleText>Outliers</VisibleText>
            </div>

            <ul class="info-list">
              <li v-for="(out, index) in dataOutliers" :key="index">
                <span class="label">{{ out.question_title }}:</span>
                <span class="value highlight">{{ out.value }}</span>
              </li>
            </ul>
          </div>

          <!-- PERFORMANCE -->
          <div v-if="dataPerformance" class="info-card">
            <div class="info-card-title">
              <span class="icon">📈</span>
              <VisibleText>Performance</VisibleText>
            </div>

            <ul class="info-list">
              <li v-for="(value, key) in dataPerformance" :key="key">
                <span class="label">{{ key }}:</span>
                <span class="value">{{ value }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
// Components
import Spinner from '@/components/UI/Spinner.vue';
import MessageBlock from '@/components/UI/Message/MessageBlock.vue';

// Services
import TextService from '@/services/textService.js';

// Vuex
import { mapGetters } from 'vuex';

// Libraries
import _ from 'lodash';

export default {
  name: 'ResponseListItemRespondentInfo',
  components: {
    Spinner,
    MessageBlock,
  },
  props: {
    responseId: {
      type: String,
      default: () => '',
    },
    clientQuestionId: {
      type: String,
      default: () => {},
    },
    show: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      TEXT_SERVICE: new TextService(this.$pigeonline),
      isFetching: false,
      data: null,
    };
  },
  computed: {
    ...mapGetters('project', {
      project: 'getProject',
    }),
    dataCategories: function () {
      if (
        !this.data ||
        !this.data.categorical_questions ||
        _.isEmpty(this.data.categorical_questions)
      )
        return null;
      return Object.values(this.data.categorical_questions).filter(Boolean);
    },
    dataOutliers: function () {
      if (!this.data || !this.data.scale_1_5_outliers || _.isEmpty(this.data.scale_1_5_outliers))
        return null;
      return Object.values(this.data.scale_1_5_outliers).filter(Boolean);
    },
    dataSubmissionTime: function () {
      if (
        !this.data ||
        !this.data.submission_time ||
        !this.data.submission_time.value ||
        _.isEmpty(this.data.submission_time.value)
      )
        return null;

      return this.data.submission_time.value;
    },
    dataPerformance: function () {
      if (
        !this.data ||
        !this.data.performance ||
        _.isEmpty(this.data.performance) ||
        this.data.performance.score == ''
      )
        return null;
      return this.data.performance;
    },
    isDataEmpty: function () {
      return !this.dataCategories && !this.dataOutliers && !this.dataPerformance;
    },
  },
  mounted() {
    if (this.show && !this.isFetching) this.fetchRespondentInfo();
  },
  methods: {
    fetchRespondentInfo: async function () {
      if (!this.data) this.isFetching = true;
      try {
        this.data = await this.TEXT_SERVICE.respondentInfo({
          project_id: this.project.id,
          response_id: this.responseId,
          client_question_id: this.clientQuestionId,
        });
      } catch (e) {
        // eslint-disable-next-line
        console.error('ResponseListItemRespondentInfo.vue:fetchRespondentInfo:: ' + e);
      } finally {
        this.isFetching = false;
      }
    },
  },
  watch: {
    show: function (val) {
      if (!val) return;
      if (this.data) return;
      this.fetchRespondentInfo();
    },
    'project.settings': function (val) {
      if (!val) return;
      if (this.show) this.fetchRespondentInfo();
    },
  },
};
</script>

<style scoped>
.respondent-info {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 0.5rem 0;
}

.info-card {
  background: #fafbff;
  border: 1px solid #e4e7f5;
  border-radius: 8px;
  padding: 1rem 1.25rem;
}

.info-card-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-size: 1rem;
  color: #3a3f73;
}

.info-card-title .icon {
  margin-right: 0.5rem;
  font-size: 1.2rem;
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: max-content 1fr;
  row-gap: 0.35rem;
  column-gap: 1rem;
}

.info-list li {
  display: contents; /* allows grid to control layout */
}

.label {
  font-weight: 500;
  color: #4a4f7d;
  white-space: nowrap; /* prevents wrapping */
}

.value {
  font-weight: 600;
  color: #1f2348;
  word-break: break-word;
}

.value.highlight {
  color: #c0392b;
  font-weight: 700;
}
</style>
